// ---------------------------------------------------------------------------
// CogoComponentRegistry — singleton registry for all component definitions.
// Components register themselves here; the canvas, palette, and inspector
// all read from this single source of truth.
// ---------------------------------------------------------------------------

import type { CogoComponentDefinition, ComponentCategory } from './types'

class CogoComponentRegistry {
  private definitions = new Map<string, CogoComponentDefinition>()

  /** Register a component definition. Overwrites any existing definition for the same type. */
  register(definition: CogoComponentDefinition): void {
    this.definitions.set(definition.type, definition)
  }

  /** Remove a component definition by type. Returns true if found and removed. */
  unregister(type: string): boolean {
    return this.definitions.delete(type)
  }

  /** Retrieve a definition by component type. Returns undefined if not registered. */
  get(type: string): CogoComponentDefinition | undefined {
    return this.definitions.get(type)
  }

  /** Return all registered definitions as an array. */
  getAll(): CogoComponentDefinition[] {
    return Array.from(this.definitions.values())
  }

  /** Return definitions filtered by category. */
  getByCategory(category: ComponentCategory): CogoComponentDefinition[] {
    return this.getAll().filter(d => d.category === category)
  }

  /** Return definitions grouped by category with labels. */
  getCategories(): { id: ComponentCategory; label: string; components: CogoComponentDefinition[] }[] {
    const cats = new Map<ComponentCategory, CogoComponentDefinition[]>()
    for (const def of this.definitions.values()) {
      if (!cats.has(def.category)) cats.set(def.category, [])
      cats.get(def.category)!.push(def)
    }
    return Array.from(cats.entries()).map(([id, components]) => ({
      id,
      label: id.charAt(0).toUpperCase() + id.slice(1),
      components,
    }))
  }

  /** Search definitions by type, label, or category (case-insensitive substring match). */
  search(query: string): CogoComponentDefinition[] {
    const q = query.toLowerCase()
    return this.getAll().filter(
      d => d.type.includes(q) || d.label.toLowerCase().includes(q) || d.category.includes(q),
    )
  }

  /** Remove all registered definitions. */
  clear(): void {
    this.definitions.clear()
  }

  /** Number of registered definitions. */
  get registeredCount(): number {
    return this.definitions.size
  }
}

/** Singleton registry instance. */
export const componentRegistry = new CogoComponentRegistry()
